import hyperdom from 'hyperdom'
import pluralize from 'pluralize'
import titlecase from 'title-case'
import {isString} from 'lodash-es'
import formats from '../../../../../../packages/dates/formats.js'
import formatDate from '../../../../../../packages/dates/formatDate.js'
import formatPrice from '../../../../../../packages/price-engine/formatPrice.js'
import responsiveRender from '../../../responsiveRender.js'

export default class TripSummary {

  constructor ({getTripDetails, departure, config}) {
    this.departure = departure
    this.getTripDetails = getTripDetails
    this.config = config
  }

  render () {
    const details = this.getTripDetails()
    return details ? <div class="trip-summary">
      <div class="trip-summary__header">Trip summary</div>
      <div>
        {this.renderFlight(details.outboundFlight, true)}
        {this.renderExtraStay(details.preAccommodation, true)}
        {this.renderTripDates()}
        {this.renderExtraStay(details.postAccommodation, false)}
        {this.renderFlight(details.returnFlight, false)}
      </div>
      <hr></hr>
      {this.renderPriceBreakdown(details)}
    </div> : undefined
  }

  renderFlight (flight, isOutbound) {
    return flight ? responsiveRender('small',
      () => this.renderFlightSmallLayout(flight, isOutbound),
      () => this.renderFlightRegularLayout(flight, isOutbound)
    ) : undefined
  }

  renderFlightRegularLayout (flight, isOutbound) {
    return <div class="trip-summary__container">
      <div class="trip-summary__icon -new-ui_plane"></div>
      <div class="trip-summary__flight-container">
        <div class="trip-summary__title-container">{isOutbound ? 'Outbound' : 'Return'} flight</div>
        <div class="o-accent-box trip-summary__flight-details">
          {this.renderDetail('Departure Date', formatDate(flight.departDate, formats.human), 'flight-departure-date')}
          {flight.departTime ? this.renderDetail('Time', flight.departTime.format('HH:mm'), 'flight-departure-time') : undefined}
          {this.renderDetail('Class', flight.isMixedCabin ? 'Mixed Cabin' : titlecase(flight.cabin), 'flight-class')}
          <div class={`trip-summary__flight-airports${flight.departTime ? '' : '--wide'}`}>
            {this.renderDetail(flight.depart, flight.departureAirport, 'flight-departure-airport')}
            <div class="u-margin-right-medium -new-ui_long-arrow-right"></div>
            {this.renderDetail(flight.arrive, flight.arrivalAirport, 'flight-arrival-airport')}
          </div>
        </div>
      </div>
    </div>
  }

  renderFlightSmallLayout (flight, isOutbound) {
    return <div class="trip-summary__container">
      <div class="trip-summary__flight-header">
        <div class="trip-summary__icon-small -new-ui_plane"></div>
        <div class="trip-summary__title-container">{isOutbound ? 'Outbound' : 'Return'} flight</div>
      </div>
      <div class="o-accent-box trip-summary__flight-details">
        <div class="trip-summary__flight-details-row u-margin-bottom-small">
          {this.renderDetail('Departure Date',
            formatDate(flight.departDate, formats.human),
            flight.departTime ? 'flight-departure-date' : 'flight-departure-date--wide'
          )}
          {flight.departTime ? this.renderDetail('Time', flight.departTime.format('HH:mm'), 'flight-departure-time') : undefined}
          {this.renderDetail('Class', flight.isMixedCabin ? 'Mixed Cabin' : titlecase(flight.cabin), 'flight-class')}
        </div>
        <div class="trip-summary__flight-airports">
          {this.renderDetail(flight.depart, flight.departureAirport, 'flight-departure-airport')}
          {this.renderDetail(flight.arrive, flight.arrivalAirport, 'flight-arrival-airport')}
        </div>
      </div>
    </div>
  }

  renderExtraStay (accommodation, isPreAccommodation) {
    if (!accommodation) {
      return undefined
    }
    const checkInDate = isPreAccommodation
      ? formatDate(this.departure.startDate.addDays(-accommodation.nights), formats.humanShort)
      : formatDate(this.departure.endDate, formats.humanShort)
    const checkOutDate = isPreAccommodation
      ? formatDate(this.departure.startDate, formats.humanShort)
      : formatDate(this.departure.endDate.addDays(accommodation.nights), formats.humanShort)
    return responsiveRender('small',
      () => this.renderExtraStaySmallLayout(accommodation, isPreAccommodation, checkInDate, checkOutDate),
      () => this.renderExtraStayRegularLayout(accommodation, isPreAccommodation, checkInDate, checkOutDate)
    )
  }

  renderExtraStayRegularLayout (accommodation, isPreAccommodation, checkInDate, checkOutDate) {
    return <div class="trip-summary__container">
      <div class="trip-summary__icon -new-ui_hotel"></div>
      <div class="trip-summary__extra-stay-container">
        <div class="trip-summary__title-container">{isPreAccommodation ? 'Pre' : 'Post'}-trip accommodation</div>
        <div class="o-accent-box trip-summary__extra-stay-details">
          {this.renderDetail('Check-in', checkInDate, 'extra-stay-check-in')}
          {this.renderDetail('Check-out', checkOutDate, 'extra-stay-check-out')}
          {this.renderDetail('Stay', pluralize('night', accommodation.nights, true), 'extra-stay-nights')}
          {this.renderDetail('Hotel', accommodation.name, 'extra-stay-hotel')}
        </div>
      </div>
    </div>
  }

  renderExtraStaySmallLayout (accommodation, isPreAccommodation, checkInDate, checkOutDate) {
    return <div class="trip-summary__container">
      <div class="trip-summary__extra-stay-header">
        <div class="trip-summary__icon-small -new-ui_hotel"></div>
        <div class="trip-summary__title-container">{isPreAccommodation ? 'Pre' : 'Post'}-trip accommodation</div>
      </div>
      <div class="o-accent-box trip-summary__extra-stay-details">
        <div class="trip-summary__extra-stay-details-row u-margin-bottom-small">
          {this.renderDetail('Check-in', checkInDate, 'extra-stay-check-in')}
          {this.renderDetail('Check-out', checkOutDate, 'extra-stay-check-out')}
        </div>
        <div class="trip-summary__extra-stay-details-row">
          {this.renderDetail('Hotel', accommodation.name, 'extra-stay-hotel')}
          {this.renderDetail('Stay', pluralize('night', accommodation.nights, true), 'extra-stay-nights')}
        </div>
      </div>
    </div>
  }

  renderTripDates () {
    return responsiveRender('small',
      () => this.renderTripDatesSmallLayout(),
      () => this.renderTripDatesRegularLayout()
    )
  }

  renderTripDatesRegularLayout () {
    return <div class="trip-summary__container">
      <div class="trip-summary__icon -new-ui_calendar"></div>
      <div class="trip-summary__trip-dates-container">
        <div class="trip-summary__title-container">Trip Dates</div>
        <div class="trip-summary__trip-dates-details">
          <div class="u-margin-right-small trip-summary__trip-start-date">{this.renderTripDate({isStartDate: true})}</div>
          <div class="u-margin-left-small trip-summary__trip-end-date">{this.renderTripDate({isStartDate: false})}</div>
          <div class="trip-summary__trip-dates__direction-icon -new-ui_long-arrow-right"></div>
        </div>
      </div>
    </div>
  }

  renderTripDatesSmallLayout () {
    return <div class="trip-summary__container">
      <div class="trip-summary__trip-dates-header">
        <div class="trip-summary__icon-small -new-ui_calendar"></div>
        <div class="trip-summary__title-container">Trip Dates</div>
      </div>
      <div class="trip-summary__trip-dates-details">
        <div class="u-margin-bottom-small trip-summary__trip-start-date">{this.renderTripDate({isStartDate: true})}</div>
        <div class="u-margin-bottom-small trip-summary__trip-end-date">{this.renderTripDate({isStartDate: false})}</div>
      </div>
    </div>
  }

  renderTripDate ({isStartDate}) {
    const departure = this.departure
    return <div class="o-accent-box trip-summary__trip-date-detail-container">
      {this.renderDetail(`Trip ${isStartDate ? 'Start' : 'End'}`, formatDate(isStartDate ? departure.startDate : departure.endDate, formats.humanShort), 'trip-date')}
      {this.renderDetail('Time', isStartDate ? departure.startTimeLocal : departure.endTimeLocal, 'trip-time')}
      {this.renderDetail('Location', `${(isStartDate ? departure.startPlace : departure.endPlace).city}`, 'trip-location')}
    </div>
  }

  renderDetail (header, detail, type) {
    const baseClass= `trip-summary-detail__${type || ''}`
    return <div class={`${baseClass}-container`}>
      <div class={`${baseClass}-header`}>{header}</div>
      <div class={`${baseClass}-detail`}>{detail}</div>
    </div>
  }

  renderPriceBreakdown ({prices, total, currency, payment: {amount, outstanding, fee = 0}}) {
    const detailedTotal = outstanding > 0 && amount > 0

    return <div class="trip-summary-price-breakdown">
      <div class="trip-summary-price-breakdown__container">
        <h4 class="trip-summary-price-breakdown__title">Price breakdown</h4>
        <div class="trip-summary-price-breakdown__price-container">
          {
            prices.filter(({price}) => !this.config.hideZeroValueMiscProductsInSummary || !!price).map(({name, isDiscount, price}) => {
              return <div class="trip-summary-price-breakdown__price-container-row">
                <div class="trip-summary-price-breakdown__price-name">{name === 'Frequent Traveller Discount' ? this.config.localisedWords.ftd : name}</div>
                <div class={{'trip-summary-price-breakdown__discount-price': isDiscount, 'trip-summary-price-breakdown__price-amount': !isDiscount}}>
                  {isDiscount ? '- ': ''}
                  {isString(price) ? price : formatPrice({amount: price, currency})}
                </div>
              </div>
            })
          }
          <hr class="trip-summary-price-breakdown__divider"></hr>
        </div>
      </div>
      {detailedTotal || (amount === 0 && outstanding === 0)
        ? this.renderPriceBreakdownSummaryRow(
          'Total Trip Cost',
          formatPrice({amount: total + fee, currency})
        )
        : undefined
      }
      {amount ? this.renderPriceBreakdownSummaryRow(
        detailedTotal ? 'Amount paid' : 'Total paid',
        (detailedTotal ? '- ' : '') + formatPrice({amount, currency, allowZero: true}),
        detailedTotal ? ['paid'] : []
      ) : undefined}
      {outstanding ? this.renderPriceBreakdownSummaryRow(
        'Amount due',
        formatPrice({amount: outstanding, currency, allowZero: true}),
        ['outstanding']
      ) : undefined}
    </div>
  }

  renderPriceBreakdownSummaryRow (title, amount, priceClass = []) {
    return <div class="trip-summary-price-breakdown__total-container">
      <h4 class="trip-summary-price-breakdown__total-title">{title}</h4>
      <div class="trip-summary-price-breakdown__total-price-container">
        <div class="trip-summary-price-breakdown__total-price-row">
          <div class="trip-summary-price-breakdown__total-price-details"></div>
          <div class={["trip-summary-price-breakdown__total-price-amount", ...priceClass]}>{amount}</div>
        </div>
      </div>
    </div>
  }
}
